import { uniqBy } from 'lodash';
import { NodeType } from '@/enums/node-type.enum';
import { isModelNode } from '@/components/Nodes/utils/nodeUtils';
import { log } from '@/logger/logger';
import { UndoRedoEntry } from '../undoRedo';
import { ImportView } from './ImportView';
import { ExportView } from './ExportView';
import { ModelBaseView } from './ModelBaseView';
import { MediaIteratorView } from './MediaIteratorView';
import { PromptView } from './PromptView';
import type { FlowGraph } from '../FlowGraph';
import type { Node, ExportData } from '@/types/node';
import type { DownloadableAsset } from '@/types/downloadable';
import type { NodeId } from 'web';

const logger = log.getLogger('NodeAssetManager');

export interface NodeWithAssets {
  nodeName: string;
  assets: DownloadableAsset[];
}

/**
 * Interface for views that support clearing all assets
 */
export interface ClearableAssetsView {
  clearAllAssets(): UndoRedoEntry;
  hasAssets(): boolean;
  /** Clears all assets except the currently selected one. Throws if node has no concept of multiple assets. */
  clearOtherAssets(): UndoRedoEntry;
  /** Returns true if the node has more than one asset. */
  hasMultipleAssets(): boolean;
}

/**
 * Checks if a node type is downloadable
 */
export function isNodeDownloadable(node: Node): boolean {
  return node.type === NodeType.Import || node.type === NodeType.Export || isModelNode(node);
}

/**
 * Checks if a node type supports clearing assets
 */
export function isNodeClearable(node: Node): boolean {
  return node.type === NodeType.Import || node.type === NodeType.MediaIterator || isModelNode(node);
}

/**
 * Manages assets for multiple nodes - collecting and clearing
 * Encapsulates graph access and View instantiation logic
 */
export class NodeAssetManager {
  constructor(private graph: FlowGraph) {}

  /**
   * Expands group nodes to include their children
   */
  private expandGroups(nodes: Node[]): Node[] {
    return uniqBy(
      nodes.flatMap((node) => {
        if (node.type === NodeType.CustomGroup) {
          return this.graph.getChildNodesOfGroup(node.id);
        }
        return [node];
      }),
      'id',
    );
  }

  /**
   * Checks if any of the selected nodes (or their children for groups) have downloadable assets
   * @param nodes - Array of nodes to check
   * @returns True if any node has downloadable assets
   */
  hasDownloadableNodes(nodes: Node[]): boolean {
    const expandedNodes = this.expandGroups(nodes);
    return expandedNodes.some((node) => {
      if (node.type === NodeType.Import) {
        const importView = new ImportView(this.graph, node.id);
        return importView.hasAssets();
      }
      if (node.type === NodeType.Export) {
        const exportView = new ExportView(this.graph, node.id);
        return exportView.isExportable();
      }
      if (isModelNode(node)) {
        const modelView = new ModelBaseView(this.graph, node.id);
        return modelView.hasAssets();
      }
      return false;
    });
  }

  /**
   * Checks if any of the selected nodes (or their children for groups) have clearable assets
   * Excludes locked nodes from consideration
   * @param nodes - Array of nodes to check
   * @returns True if any unlocked node has clearable assets
   */
  hasClearableAssets(nodes: Node[]): boolean {
    const expandedNodes = this.expandGroups(nodes);
    return expandedNodes.some((node) => {
      if (this.graph.getEffectiveIsLocked(node.id)) {
        return false;
      }
      const view = this.getClearableView(node);
      return view?.hasAssets() ?? false;
    });
  }

  /**
   * Gets a ClearableAssetsView for a node if it supports clearing
   */
  private getClearableView(node: Node): ClearableAssetsView | null {
    if (node.type === NodeType.Import) {
      return new ImportView(this.graph, node.id);
    }
    if (node.type === NodeType.MediaIterator) {
      return new MediaIteratorView(this.graph, node.id);
    }
    if (isModelNode(node)) {
      return new ModelBaseView(this.graph, node.id);
    }
    if (node.type === NodeType.PromptV3) {
      return new PromptView(this.graph, node.id);
    }
    return null;
  }

  /**
   * Checks if any of the selected nodes (or their children for groups) have multiple clearable assets
   * Excludes locked nodes from consideration
   * @param nodes - Array of nodes to check
   * @returns True if any unlocked node has more than one asset
   */
  hasClearableOtherAssets(nodes: Node[]): boolean {
    const expandedNodes = this.expandGroups(nodes);
    return expandedNodes.some((node) => {
      if (this.graph.getEffectiveIsLocked(node.id)) {
        return false;
      }
      const view = this.getClearableView(node);
      return view?.hasMultipleAssets() ?? false;
    });
  }

  /**
   * Shared implementation for clearing assets: expands groups, skips locked nodes,
   * batches undo entries inside graph.edit(), then runs updateNodeOutputs + loadResources.
   * Parameterised by predicate and clear action to support "clear all" vs "clear other".
   */
  private clearAssetsFromNodesWith(
    nodes: Node[],
    shouldClear: (view: ClearableAssetsView) => boolean,
    clear: (view: ClearableAssetsView) => UndoRedoEntry,
    errorContext: string,
  ): void {
    const expandedNodes = this.expandGroups(nodes);
    const clearedNodeIds: NodeId[] = [];

    this.graph.edit(() => {
      const combinedEntry = UndoRedoEntry.empty();

      for (const node of expandedNodes) {
        if (this.graph.getEffectiveIsLocked(node.id)) {
          continue;
        }

        const view = this.getClearableView(node);
        if (view && shouldClear(view)) {
          try {
            combinedEntry.add(clear(view));
            clearedNodeIds.push(node.id);
          } catch (error) {
            logger.error(`Failed to ${errorContext} from node ${node.id}:`, error);
          }
        }
      }

      return combinedEntry;
    }, false);

    if (clearedNodeIds.length > 0) {
      for (const nodeId of clearedNodeIds) {
        this.graph.updateNodeOutputs(nodeId, false);
      }
      this.graph.loadResources();
    }
  }

  /**
   * Clears all assets from nodes that support clearing
   * Skips locked nodes to preserve their assets
   * All clears are batched into a single undo/redo entry
   * Post-processing (loadResources, updateNodeOutputs) is run once after the edit
   * @param nodes - Array of nodes to clear assets from
   */
  clearAssetsFromNodes(nodes: Node[]) {
    this.clearAssetsFromNodesWith(
      nodes,
      (view) => view.hasAssets(),
      (view) => view.clearAllAssets(),
      'clear assets',
    );
  }

  /**
   * For each node, keeps only the currently selected asset and removes all others.
   * Skips locked nodes. Only affects nodes that have multiple assets.
   * All clears are batched into a single undo/redo entry.
   * @param nodes - Array of nodes to clear other assets from
   */
  clearOtherAssetsFromNodes(nodes: Node[]) {
    this.clearAssetsFromNodesWith(
      nodes,
      (view) => view.hasMultipleAssets(),
      (view) => view.clearOtherAssets(),
      'clear other assets',
    );
  }

  /**
   * Checks if an Export node's input is connected to a downloadable node that's also being downloaded
   * @param exportNodeId - The Export node ID to check
   * @param downloadableNodeIds - Set of downloadable node IDs in the selection
   * @returns True if the Export's input source is also being downloaded
   */
  private isExportInputAlsoBeingDownloaded(exportNodeId: NodeId, downloadableNodeIds: Set<NodeId>): boolean {
    const exportData = this.graph.store.getNodeDataNullable<ExportData>(exportNodeId);
    const inputSourceNodeId = exportData?.inputNode?.nodeId;

    // Return true if the export's input comes from another downloadable node in selection
    return inputSourceNodeId !== undefined && downloadableNodeIds.has(inputSourceNodeId);
  }

  /**
   * Collects downloadable assets from the provided nodes
   * @param nodes - Array of nodes to collect assets from
   * @param recipeId - Recipe ID needed for Export nodes with computed videos
   * @param saveRecipe - Function to save recipe before downloading Export node videos
   * @returns Array of nodes with their downloadable assets
   */
  async collectAssetsFromNodes(
    nodes: Node[],
    recipeId: string,
    saveRecipe: () => Promise<void>,
  ): Promise<NodeWithAssets[]> {
    const expandedNodes = this.expandGroups(nodes);

    const downloadableNodeIds = new Set(expandedNodes.filter(isNodeDownloadable).map((node) => node.id));

    const nodesWithAssets: NodeWithAssets[] = [];

    for (const node of expandedNodes) {
      if (!isNodeDownloadable(node)) {
        continue;
      }

      if (node.type === NodeType.Export && this.isExportInputAlsoBeingDownloaded(node.id, downloadableNodeIds)) {
        continue;
      }

      let assets: DownloadableAsset[] = [];

      try {
        if (node.type === NodeType.Import) {
          const importView = new ImportView(this.graph, node.id);
          assets = importView.getFilesList();
        } else if (node.type === NodeType.Export) {
          const exportView = new ExportView(this.graph, node.id);
          if (exportView.isExportable()) {
            const { fileInfo } = await exportView.resolveDownloadableFile(recipeId, saveRecipe);
            if (fileInfo) {
              assets = [fileInfo];
            }
          }
        } else if (isModelNode(node)) {
          const modelBaseView = new ModelBaseView(this.graph, node.id);
          assets = modelBaseView.getAllDownloadableAssets();
        }
      } catch (error) {
        logger.error(`Failed to collect assets from node ${node.id}:`, error);
      }

      if (assets.length > 0) {
        nodesWithAssets.push({
          nodeName: node.data.name || node.id,
          assets,
        });
      }
    }

    return nodesWithAssets;
  }
}
